/**
 * @file MenuItemDialog.tsx
 * @description Окно пункта меню (phase19.16 Р1; SB15, SB75, SB81;
 * uiux-design-r1 §3 «Окно пункта меню»): куда ведёт (виды из реестра),
 * цель, подпись со счётчиком (подставляется из названия цели), где стоит
 * (наверху или внутри пункта). На 1280 — окно 560px, на телефоне — весь экран.
 * В подвале (приёмка Р2а, SB113) пункт создаётся в своей колонке — «Где» нет.
 */

import { SITE_MENU_LINK_TYPES, SITE_MENU_LABEL_MAX, type SiteMenuLocation } from "@chaiberi/shared";
import { useEffect, useState } from "react";

import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { SelectField } from "../../ui/SelectField";
import { MenuTargetField } from "./MenuTargetField";
import { useMenuTargets } from "../../../hooks/site-view/useMenuTargets";
import { emptyMenuForm, menuFormFromItem, validateMenuForm, type MenuItemForm } from "../../../lib/site-view/menu-form";
import { MENU_LABEL_SOFT_MAX, MENU_LINK_TYPE_META } from "../../../lib/site-view/menu-link-types";
import type { SiteMenuItem } from "../../../lib/site-view/types";

/** Что открыто: новый пункт в месте или правка пункта. */
export type MenuDialogMode =
  | {
      kind: "create";
      location: SiteMenuLocation;
      parentId: string | null;
      linkType?: MenuItemForm["linkType"];
      /** Место задано (подвал: пункт всегда в своей колонке, SB113) — «Где» не спрашиваем. */
      lockParent?: boolean;
    }
  | { kind: "edit"; item: SiteMenuItem; nested: boolean };

interface Props {
  mode: MenuDialogMode | null;
  /** Пункты верхнего уровня, в которые можно вложить (для «Где»). */
  parents: SiteMenuItem[];
  onClose: () => void;
  onSubmit: (form: MenuItemForm, parentId: string | null) => Promise<boolean>;
}

export function MenuItemDialog({ mode, parents, onClose, onSubmit }: Props) {
  const targets = useMenuTargets();
  const [form, setForm] = useState<MenuItemForm>(emptyMenuForm());
  const [parentId, setParentId] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!mode) return;
    setTouched(false);
    if (mode.kind === "edit") {
      setForm(menuFormFromItem(mode.item));
    } else {
      setForm(emptyMenuForm(mode.linkType));
      setParentId(mode.parentId);
    }
  }, [mode]);

  const nested = mode?.kind === "edit" ? mode.nested : parentId !== null;
  const types = SITE_MENU_LINK_TYPES.filter((t) => !(nested && MENU_LINK_TYPE_META[t].topOnly));
  const errors = touched ? validateMenuForm(form) : {};
  const patch = (next: Partial<MenuItemForm>) => setForm((prev) => ({ ...prev, ...next }));

  async function submit() {
    setTouched(true);
    if (Object.keys(validateMenuForm(form)).length > 0) return;
    setBusy(true);
    const ok = await onSubmit(form, parentId);
    setBusy(false);
    if (ok) onClose();
  }

  return (
    <Dialog open={mode !== null} onOpenChange={(open) => (!open ? onClose() : null)}>
      <DialogContent fullScreen className="sm:max-w-[35rem]">
        <DialogHeader>
          <DialogTitle>{mode?.kind === "edit" ? "Пункт меню" : "Новый пункт меню"}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 overflow-y-auto">
          <div className="flex flex-col gap-1">
            <Label htmlFor="menu-type">Куда ведёт</Label>
            <SelectField
              id="menu-type"
              ariaLabel="Куда ведёт пункт"
              value={form.linkType}
              options={types.map((t) => ({ value: t, label: MENU_LINK_TYPE_META[t].label }))}
              onChange={(value) => patch({ linkType: value as MenuItemForm["linkType"], targetId: null })}
            />
          </div>
          <MenuTargetField form={form} errors={errors} targets={targets} onChange={patch} />
          <div className="flex flex-col gap-1">
            <Label htmlFor="menu-label">{form.linkType === "GROUP" ? "Заголовок колонки (можно без него)" : "Подпись"}</Label>
            <Input id="menu-label" value={form.label} maxLength={SITE_MENU_LABEL_MAX} aria-invalid={!!errors.label} onChange={(e) => patch({ label: e.target.value })} />
            <span className="type-caption text-on-surface-variant tabular-nums">
              {form.label.length}/{SITE_MENU_LABEL_MAX}
              {form.label.length > MENU_LABEL_SOFT_MAX ? " · длинная подпись займёт много места в шапке — лучше до 20 знаков" : ""}
            </span>
            {errors.label ? <p className="type-caption text-destructive">{errors.label}</p> : null}
          </div>
          {mode?.kind === "create" && !mode.lockParent && !MENU_LINK_TYPE_META[form.linkType].topOnly ? (
            <div className="flex flex-col gap-1">
              <Label htmlFor="menu-parent">Где</Label>
              <SelectField
                id="menu-parent"
                ariaLabel="Где стоит пункт"
                value={parentId ?? ""}
                placeholder="Верхний уровень"
                options={[{ value: "", label: "Верхний уровень" }, ...parents.map((p) => ({ value: p.id, label: `Внутри «${p.label ?? p.target.title}»` }))]}
                onChange={(value) => setParentId(value === "" ? null : value)}
              />
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button loading={busy} onClick={() => void submit()}>
            {mode?.kind === "edit" ? "Сохранить" : "Добавить пункт"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
